"use client";

import Autoplay from "embla-carousel-autoplay";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { useCallback, useEffect, useState } from "react";
import { useSiteSettings } from "@/features/settings/ui/hooks/useSiteSettings";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics/product-analytics";
import { Image } from "@/lib/client/components/Image";
import { placeholderImage } from "@/lib/shared/constants";
import type { MediaDisplay } from "@/lib/shared/types/interfaces";
import type { CarouselApi } from "@/ui";
import {
  AspectRatio,
  Button,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  cn,
  Grid,
} from "@/ui";

interface IImageSliderProps {
  images: MediaDisplay[];
  video?: MediaDisplay;
  width?: number;
  quality?: number;
  onClick?: () => void;
  onMediaClick?: (index: number) => void;
  jumpToIndex?: number;
  ratio?: number;
  hasThumb?: boolean;
  className?: string;
  autoPlay?: boolean;
  canZoom?: boolean;
  isModalOpened?: boolean;
  onChange?: (index: number) => void;
  fallbackAlt?: string;
}

/**
 * ImageSlider component.
 */
export const ImageSlider = (props: IImageSliderProps) => {
  const { imageRatio: settingsImageRatio } = useSiteSettings();
  const {
    images,
    video,
    onClick,
    onMediaClick,
    jumpToIndex,
    width = 500,
    quality = 75,
    ratio = settingsImageRatio,
    hasThumb = false,
    autoPlay = false,
    canZoom = false,
    isModalOpened = false,
    className,
    onChange,
    fallbackAlt,
  } = props;

  const [thumbsApi, setThumbsApi] = useState<CarouselApi>();
  const [mainApi, setMainApi] = useState<CarouselApi>();
  const mainImageSizes = hasThumb
    ? "(max-width: 1024px) 100vw, 58vw"
    : "(max-width: 1024px) 100vw, 65vw";

  const onThumbClick = useCallback(
    (index: number) => {
      if (!(mainApi && thumbsApi)) return;
      mainApi.scrollTo(index);
      if (onChange) {
        onChange(index);
      }
    },
    [mainApi, thumbsApi, onChange],
  );

  const onSelect = useCallback(() => {
    if (!(mainApi && thumbsApi)) return;
    thumbsApi.scrollTo(mainApi.selectedScrollSnap());
    if (onChange) {
      onChange(mainApi.selectedScrollSnap());
    }
  }, [mainApi, thumbsApi, onChange]);

  useEffect(() => {
    if (jumpToIndex !== undefined && jumpToIndex >= 0) {
      if (!mainApi) return;
      mainApi.scrollTo(jumpToIndex);
    }
  }, [jumpToIndex, mainApi]);

  useEffect(() => {
    if (!mainApi) return;
    onSelect();
    mainApi.on("select", onSelect);
    mainApi.on("reInit", onSelect);
  }, [mainApi, onSelect]);

  useEffect(() => {
    if (!mainApi) return;
    if (isModalOpened) {
      setTimeout(() => {
        mainApi.reInit();
      }, 500);
    }
  }, [mainApi, isModalOpened]);

  // Combine images and video - video appears after all images
  const displayItems: MediaDisplay[] = images?.length
    ? [...images, ...(video ? [video] : [])]
    : [placeholderImage];

  return (
    <Grid className={cn("relative grid-cols-5 gap-x-2", className)}>
      {hasThumb && (
        <Carousel
          setApi={setThumbsApi}
          orientation={"vertical"}
          plugins={[WheelGesturesPlugin()]}
          opts={{
            containScroll: "keepSnaps",
            dragFree: true,
            align: "center",
          }}
          className="relative col-span-1"
        >
          <CarouselContent className={"contain-size"}>
            {displayItems.map((item, index) => (
              <CarouselItem
                key={item.id || `thumb-${index}`}
                className="w-full"
              >
                <Button
                  key={item.id || `thumb-${index}`}
                  onClick={() => onThumbClick(index)}
                  className={cn("h-fit w-full border-0 p-0")}
                >
                  {item === video ? (
                    // Video thumbnail - maintain video's own aspect ratio, full width
                    <div
                      className="relative w-full rounded-md overflow-hidden cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        trackEvent(AnalyticsEvents.PRODUCT_LIGHTBOX_OPENED, {
                          image_index: index,
                          media_type: "video",
                        });
                        if (onMediaClick) {
                          onMediaClick(index);
                        }
                      }}
                    >
                      <video
                        src={video.src}
                        muted
                        playsInline
                        className="w-full h-auto pointer-events-none"
                      />
                      {/* Play icon overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60">
                          <svg
                            className="h-4 w-4 text-white ml-0.5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <AspectRatio
                      ratio={ratio}
                      className="bg-muted"
                    >
                      <Image
                        src={item.thumbnailSrc || item.src}
                        alt={item.alt || fallbackAlt || "Product image - TUTULY jewellery"}
                        width={200}
                        height={200 * ratio}
                        quality={75}
                        sizes="(max-width: 1024px) 20vw, 10vw"
                        loading="lazy"
                        decoding="async"
                        style={{
                          objectFit: "cover",
                        }}
                        className="h-full cursor-pointer rounded-md object-center"
                      />
                    </AspectRatio>
                  )}
                </Button>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      )}

      <Carousel
        setApi={setMainApi}
        orientation={"horizontal"}
        plugins={autoPlay ? [Autoplay(), WheelGesturesPlugin()] : [WheelGesturesPlugin()]}
        opts={{
          loop: true,
        }}
        className={cn(hasThumb ? "col-span-4" : "col-span-5", "w-full overflow-hidden")}
        onClick={onClick}
      >
        <CarouselContent className={"w-full"}>
          {displayItems.map((item, index) => (
            <CarouselItem
              key={item.id || `main-${index}`}
              className="w-full"
            >
              {item === video ? (
                // Video player - maintain video's own aspect ratio, full width
                <div
                  className={cn("w-full rounded-md", canZoom && "cursor-pointer")}
                  onClick={() => {
                    if (onMediaClick) {
                      onMediaClick(index);
                    }
                  }}
                >
                  <video
                    src={item.src}
                    controls
                    playsInline
                    className="w-full h-auto rounded-md"
                    poster={item.thumbnailSrc || item.src}
                  />
                </div>
              ) : (
                <AspectRatio
                  ratio={ratio}
                  className="bg-muted"
                >
                  <Image
                    src={item.src}
                    alt={item.alt || fallbackAlt || "Product image - TUTULY jewellery"}
                    onClick={() => {
                      trackEvent(AnalyticsEvents.PRODUCT_IMAGE_ZOOMED, {
                        image_index: index,
                        media_type: item === video ? "video" : "image",
                      });
                      if (onMediaClick) {
                        onMediaClick(index);
                      }
                    }}
                    width={width}
                    height={width * ratio}
                    quality={quality}
                    loading={index === 0 ? "eager" : "lazy"}
                    priority={index === 0}
                    fetchPriority={index === 0 ? "high" : "auto"}
                    sizes={mainImageSizes}
                    decoding="async"
                    style={{
                      objectFit: "cover",
                    }}
                    className={cn(
                      canZoom ? "cursor-pointer" : "",
                      "h-full transform rounded-md object-center transition-transform",
                    )}
                  />
                </AspectRatio>
              )}
            </CarouselItem>
          ))}
        </CarouselContent>
        {displayItems.length > 2 && (
          <>
            <CarouselPrevious
              variant="outline"
              size="icon-lg"
              className="border-0  hover:bg-white/20"
            />
            <CarouselNext
              variant="outline"
              size="icon-lg"
              className="border-0  hover:bg-white/20"
            />
          </>
        )}
      </Carousel>
    </Grid>
  );
};
